import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Portal,
  Tag,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import useGetUsersList from '@hooks/useGetUsersList';
import UserSelect from '@components/UserSelect';
import Loading from '@components/Loading';
import type { UserType } from '@customTypes/user';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

type Props = {
  executionId: string;
  handleClose: () => void;
};

const ManageUsersDialog = ({ executionId, handleClose }: Props) => {
  const {
    getUsers,
    users,
  } = useGetUsersList();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);

  const onSaveClick = async () => {
    setSaving(true);

    const params = {
      user_ids: selectedUsers.map((user) => user.id),
    };

    try {
      const response = await api.put(`/campaign_execution/${executionId}/users`, params);

      if (response?.status === 200) {
        toast.success('Usuários alterados com sucesso!');
      }
    } catch (error) {
      toast.error('Erro ao alterar os usuários. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to update campaign execution users'), {
        error,
        executionId,
        params,
      });
    } finally {
      setSaving(false);
    }
  };

  const getCurrentUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/campaign_execution/${executionId}/users`);
      setSelectedUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar os usuários deste atendimento. Tente novamente mais tarde.');
      datadogRum.addError(new Error('Fail to get campaign execution users'), {
        error,
        executionId,
      });
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  const handleWithSelectUser = async (user: UserType | null) => {
    if (!user) return;
    if (selectedUsers.find((u) => u.id === user.id)) return;

    setSelectedUsers((prev) => [...prev, user])
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  useEffect(() => {
    getCurrentUsers();
    getUsers({});
  }, [getUsers, getCurrentUsers]);

  return (
    <Dialog.Root
      size="md"
      placement="center"
      open
      onOpenChange={handleClose}
      closeOnInteractOutside={false}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                Alterar usuários
              </Dialog.Title>
            </Dialog.Header>

            {loading ? (
              <Loading />
            ) : (
              <Dialog.Body>
                <UserSelect
                  errors={null}
                  users={users}
                  onSearch={async (text) => await getUsers({ name: text })}
                  onChange={handleWithSelectUser}
                />
                {selectedUsers.length > 0 && (
                  <Flex gap={1} flexWrap="wrap" mt={2}>
                    {selectedUsers.map((user) => (
                      <Tag.Root key={user.id} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
                        <Tag.Label>{user.name}</Tag.Label>
                        <Tag.EndElement>
                          <Tag.CloseTrigger
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveUser(user.id)}
                          />
                        </Tag.EndElement>
                      </Tag.Root>
                    ))}
                  </Flex>
                )}
              </Dialog.Body>
            )}

            <Dialog.Footer>
              <Button
                variant="outline"
                disabled={saving}
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                bg="green.700"
                color="white"
                onClick={onSaveClick}
                loading={saving}
              >
                Salvar alterações
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ManageUsersDialog;
